import { useState } from "react";
import type { SceneState } from "../../../../shared/api-contract";
import { useViewer } from "../../../../state/ViewerProvider";
import { useDomainState } from "../../../../state/editor/useEditor";
import { downloadBlob } from "../../../../util/download";
import { Icon } from "../../../kit";
import { EmbedPanel } from "../../panels/EmbedPanel";
import { BrandingPanel } from "../../panels/BrandingPanel";
import { ConfiguratorSwitch } from "../../configurator/ConfiguratorSwitch";
import { EXPORT_PRESETS, SHOT_LIST, VIDEO_PRESETS, fileStem, type ExportPreset } from "../exportPresets";
import { renderFramed } from "../fillFrame";
import { ActionRow, Note, Section } from "./ui";
import { OPEN_GOLDSMITH_EVENT } from "../events";
import { useSceneFileActions } from "../../scene/useSceneFileActions";


/**
 * Export, as "where is this going?" — a jeweller knows the destination, not
 * the pixel size. Every destination is its marketplace's own recommendation
 * (exportPresets.ts); the ring is framed to that frame's share by fillFrame.ts
 * after rendering, and Amazon's white is composited host-side so it is exactly
 * 255,255,255.
 */
export function OutputPanel() {
  const { client } = useViewer();
  const r = client.ring;
  const models = useDomainState("models");
  const scene = useDomainState("scene") as SceneState | undefined;
  const [busy, setBusy] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [allAngles, setAllAngles] = useState(false);
  const [seeThrough, setSeeThrough] = useState(false);
  const stem = fileStem(models[0]?.name);

  const job = async (id: string, fn: () => Promise<string>) => {
    if (busy) return;
    setBusy(id);
    setError(null);
    setDone(null);
    try {
      setDone(await fn());
    } catch (e) {
      setError(e instanceof Error ? e.message : "That export failed.");
    } finally {
      setBusy(null);
    }
  };

  const still = (p: ExportPreset) =>
    job(p.id, async () => {
      const background = seeThrough && p.background !== "white" ? "transparent" : p.background;
      const format = background === "transparent" ? "png" : p.format;
      const ext = format === "png" ? "png" : "jpg";
      const one = async (suffix: string) => {
        const { blob } = await renderFramed(r, { width: p.width, height: p.height, fill: p.fill, background, format });
        downloadBlob(blob, `${stem}-${p.id}${suffix}.${ext}`);
      };
      if (!allAngles) {
        await one("");
        return `Saved your ${p.label} image.`;
      }
      // Walk the shot list, then put the camera back exactly where it was.
      const back = await r.addCameraView("__export_return");
      try {
        for (const s of SHOT_LIST) {
          await r.setCameraPreset(s.camera);
          await one(`-${s.id}`);
        }
      } finally {
        await Promise.resolve(r.flyToCameraView(back.id, 0)).catch(() => undefined);
        await Promise.resolve(r.deleteCameraView(back.id)).catch(() => undefined);
      }
      return `Saved ${SHOT_LIST.length} ${p.label} images — hero, front, side and top.`;
    });

  const video = (v: (typeof VIDEO_PRESETS)[number]) =>
    job(`video:${v.id}`, async () => {
      const blob = await r.exportTurntableVideo({ width: v.width, height: v.height, fps: 30, durationMs: v.seconds * 1000, revolutions: 1, format: "mp4" });
      const ext = blob.type.includes("webm") ? "webm" : "mp4";
      downloadBlob(blob, `${stem}-${v.id}-360.${ext}`);
      return `Saved your ${v.label} video.`;
    });

  const model3d = () =>
    job("3d", async () => {
      const blob = await r.exportGLB({ compress: true });
      downloadBlob(blob, `${stem}.glb`);
      return "Saved the 3D model. Upload it to Shopify as product media, or to your site's 3D viewer.";
    });

  const transparentScene = scene?.backgroundSource === "transparent";

  return (
    <div data-basic-output>
      <Section label="Photo — where is it going?">
        <div className="mb-2 flex flex-wrap gap-1.5">
          <Toggle on={allAngles} onChange={setAllAngles} label="All 4 angles" data-all-angles />
          <Toggle on={seeThrough} onChange={setSeeThrough} label="See-through" data-see-through />
        </div>
        <div className="divide-y divide-ed-border overflow-hidden rounded-[10px] border border-ed-border">
          {EXPORT_PRESETS.map((p) => (
            <button
              key={p.id}
              onClick={() => void still(p)}
              disabled={!!busy}
              className="flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-ed-hover disabled:cursor-not-allowed disabled:opacity-60"
              data-export-preset={p.id}
            >
              <span className="min-w-0 flex-1">
                <span className="block text-[13px] font-medium leading-[17px] text-ed-text">{p.label}</span>
                <span className="block text-[12px] leading-4 text-ed-text-2">{p.use}</span>
              </span>
              <span className="shrink-0 text-[12px] tabular-nums text-ed-text-2">
                {p.width}×{p.height}
              </span>
              <Icon
                name={busy === p.id ? "progress_activity" : "download"}
                size={18}
                className={`shrink-0 ${busy === p.id ? "animate-spin text-ed-text-2" : "text-ed-text-3"}`}
              />
            </button>
          ))}
        </div>
        {seeThrough ? <p className="mt-1.5 text-[12px] leading-4 text-ed-text-2">See-through saves a PNG with no background (Amazon stays pure white).</p> : null}
        {transparentScene && !seeThrough ? (
          <p className="mt-1.5 text-[12px] leading-4 text-ed-text-2">Your background is see-through; photos keep it unless the destination needs white.</p>
        ) : null}
      </Section>

      <Section label="360° video">
        <div className="flex flex-col gap-1.5">
          {VIDEO_PRESETS.map((v) => (
            <ActionRow
              key={v.id}
              icon="360"
              label={v.label}
              sub={v.use}
              busy={busy === `video:${v.id}`}
              disabled={!!busy && busy !== `video:${v.id}`}
              onClick={() => void video(v)}
              trailing={<Icon name="download" size={18} className="shrink-0 text-ed-text-3" />}
              data-video-preset={v.id}
            />
          ))}
        </div>
      </Section>

      <Section label="3D for your website">
        <ActionRow
          icon="deployed_code"
          label="3D model for Shopify & websites"
          sub="Compressed and ready to upload"
          busy={busy === "3d"}
          disabled={!!busy && busy !== "3d"}
          onClick={() => void model3d()}
          trailing={<Icon name="download" size={18} className="shrink-0 text-ed-text-3" />}
          data-export-3d
        />
      </Section>

      <SceneFileSection />

      <Section label="AI photos & video">
        <ActionRow
          icon="auto_awesome"
          label="AI Photoshoot"
          sub="Send this piece to TheGoldsmith for model and lifestyle shots"
          onClick={() => window.dispatchEvent(new Event(OPEN_GOLDSMITH_EVENT))}
        />
      </Section>

      {busy ? <Note>Preparing your file — this can take a few seconds…</Note> : null}
      {done && !busy ? <Note tone="ok">{done}</Note> : null}
      {error ? <Note tone="error">{error}</Note> : null}
    </div>
  );
}

/**
 * The whole setup as one file (viewer v65): save it, open one, or save the
 * ring with its setup inside. Shown only on a viewer that has them — the
 * three calls do not exist on an older one, and a call to a missing method
 * never answers.
 */
function SceneFileSection() {
  const a = useSceneFileActions();
  if (!a.available) return null;
  const other = (key: string) => !!a.busy && a.busy !== key;
  return (
    <Section label="Save your whole setup">
      <div className="flex flex-col gap-1.5">
        <ActionRow
          icon="save"
          label="Save scene file"
          sub="Everything except the ring — lights, metals, diamonds, camera, effects, configurator"
          busy={a.busy === "save"}
          disabled={other("save")}
          onClick={() => void a.save()}
          trailing={<Icon name="download" size={18} className="shrink-0 text-ed-text-3" />}
          data-scene-save
        />
        <ActionRow
          icon="folder_open"
          label="Open scene file"
          sub="Replaces the current look. You can undo."
          busy={a.busy === "open"}
          disabled={other("open")}
          onClick={() => void a.open()}
          data-scene-open
        />
        <ActionRow
          icon="deployed_code"
          label="Ring + scene"
          sub={a.hasModel ? "The ring with its whole setup inside, for sharing or uploading" : "Add a ring first"}
          busy={a.busy === "ring"}
          disabled={!a.hasModel || other("ring")}
          onClick={() => void a.ringWithScene()}
          trailing={<Icon name="download" size={18} className="shrink-0 text-ed-text-3" />}
          data-scene-ring
        />
      </div>
    </Section>
  );
}

function Toggle({
  on,
  onChange,
  label,
  ...data
}: {
  on: boolean;
  onChange: (v: boolean) => void;
  label: string;
  [k: `data-${string}`]: string | boolean | undefined;
}) {
  return (
    <button
      onClick={() => onChange(!on)}
      aria-pressed={on}
      className={`flex h-[30px] items-center gap-1.5 rounded-lg border px-2.5 text-[12px] transition-colors ${
        on ? "border-ed-gold bg-ed-active font-semibold text-ed-text" : "border-ed-border font-medium text-ed-text-2 hover:border-ed-line"
      }`}
      {...data}
    >
      <Icon name={on ? "check_box" : "check_box_outline_blank"} size={16} className={on ? "text-ed-gold" : "text-ed-text-3"} />
      {label}
    </button>
  );
}

/**
 * Share — put the piece on a website (embed code and link) with the brand's
 * own logo. Both halves are the Advanced panels themselves, not copies: a
 * second copy of a setting is how two controls over one value drift apart.
 */
export function SharePanel() {
  return (
    <div data-basic-share className="-mx-[18px]">
      <p className="px-[18px] text-[12px] leading-[17px] text-ed-text-2">
        Put this piece on your own website — it spins, zooms and shows your logo.
      </p>
      <ConfiguratorSwitch />
      <EmbedPanel title="On your website" pad="px-[18px] pt-5 pb-2" />
      <BrandingPanel title="Your logo" pad="px-[18px] pt-5 pb-2" />
    </div>
  );
}
